import React, { useState, useContext, useEffect, ReactNode } from 'react';
import client from './shared/ApiClient';
import { useHistory, useLocation } from 'react-router-dom';
import { consoleLogInDev } from 'shared';
import { UserContextUser, IRole, BLOCKED_PAYMENT_STATUSES } from 'models';
import { Environment, MixPanelKey } from 'environmentVariables';
import mixpanel from 'mixpanel-browser';

mixpanel.init(MixPanelKey);

interface IContextProps {
    children: ReactNode;
}

export interface UserContextType {
    user?: UserContextUser;
    role?: IRole;
    refreshUser: (onComplete?: (user?: UserContextUser) => void) => Promise<UserContextUser>;
    refreshAlreadyLoggedInUser: (onComplete?: () => void) => void;
    savedProviders?: number[];
    updateSavedProviders: (providerId: number) => void;
    fetchUser: () => Promise<UserContextUser>;
    tempToken: string;
    setTempToken: (x: string) => void;
    getDisplayText: (key: string, fallbackValue?: string) => string;
    logout: (redirect?: string) => void;
    hasPermission: (permissionType: PermissionType, addressId?: number) => boolean;
    isPaymentBlocked: boolean;
}

export enum PermissionType {
    PostJob = 1,
    ManagePayments = 2,
    ManageAccount = 3,
}

export const UserContext = React.createContext<UserContextType>({} as UserContextType);

export function UserProvider({ children }: IContextProps) {
    const [user, setUser] = useState<UserContextUser>();
    const [role, setRole] = useState<IRole>();
    const [savedProviders, setSavedProviders] = useState<number[]>([]);
    const [tempToken, setTempToken] = useState<string>('');
    const [businessTypeTranslations, setBusinessTypeTranslations] = useState<{ [key: string]: string }>();
    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        if (localStorage.getItem('token')) {
            refreshUser();
        }
    }, []);

    const excludedRoles = ['marketer', 'medical_provider'];
    useEffect(() => {
        if (
            user &&
            !user.admin &&
            !excludedRoles.includes(role ?? '') &&
            !user.is_in_supported_region &&
            !['/unsupportedregion', '/', '/family/signup/'].includes(location.pathname)
        ) {
            logout('unsupportedregion');
        }
    }, [user, location]);

    useEffect(() => {
        if (user) client('api/business-type-translations/').then(setBusinessTypeTranslations).catch(consoleLogInDev);
    }, [user]);

    function initMixPanel(user: UserContextUser) {
        mixpanel.identify(user.id.toString());
        mixpanel.people.set({
            $name: user.first_name + ' ' + user.last_name,
            $email: user.email,
            $payment_setup: user.customer_id !== null || user.payment_information?.length > 0,
            $payment_setup_verified: !user.payment_information?.some((x) => x.status === 'NEW' && x.is_default),
            $points: user.points,
        });
    }

    function logout(redirect?: string) {
        if (localStorage.getItem('token')) client('api/auth/logout/', { method: 'POST' });
        const defaultRedirect = '/';
        redirect = redirect || defaultRedirect;
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        setUser(undefined);
        setRole(undefined);
        setSavedProviders([]);
        history.push(redirect);
        window.$chatwoot?.reset();
    }

    async function fetchUser() {
        try {
            const response = await client('api/users/identity/');
            const apiUser = response;
            setUser(apiUser);
            initMixPanel(apiUser);
            return apiUser;
        } catch (error) {
            if (Environment.toLowerCase() !== 'development') {
                localStorage.removeItem('token');
            }
            window.location.reload();
        }
    }

    async function refreshUser(onComplete?: (user: UserContextUser) => void) {
        try {
            const apiUser = await fetchUser();
            if (!apiUser) {
                if (Environment.toLowerCase() !== 'development') {
                    localStorage.removeItem('token');
                }
                window.location.reload();
            }
            updateRole(apiUser, true);
            if (onComplete) {
                onComplete(apiUser);
            }
            return apiUser;
        } catch (error) {
            if (Environment.toLowerCase() !== 'development') {
                localStorage.removeItem('token');
            }
            window.location.reload();
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function updateRole(apiUser: any, trackLogin = false) {
        if (!apiUser) {
            setRole(undefined);
        } else if (apiUser?.sitter && apiUser.sitter.length) {
            setRole('sitter');
        } else if (apiUser?.businesses_active && apiUser?.businesses_active?.length) {
            setRole('business_active');
            if (trackLogin) {
                trackLoginActivity('BUSINESS');
            }
        } else if (apiUser?.businesses && apiUser?.businesses?.length) {
            setRole('business');
            if (trackLogin) {
                trackLoginActivity('BUSINESS');
            }
        } else if (apiUser?.family && apiUser?.family?.length) {
            setRole('family');
            if (trackLogin) {
                trackLoginActivity('FAMILY');
            }
        } else if (apiUser?.is_superuser && apiUser.is_superuser) {
            setRole('admin');
        } else if (apiUser?.has_outreach_permissions && apiUser.has_outreach_permissions) {
            setRole('marketer');
        } else if (apiUser?.role === 'MEDICAL_PROVIDER') {
            setRole('medical_provider');
            history.push(`/medical-provider/${apiUser.id}`);
        } else {
            localStorage.clear();
            history.push('/');
        }
    }

    async function refreshAlreadyLoggedInUser(onComplete?: () => void) {
        const apiUser = await fetchUser();
        updateRole(apiUser);
        if (onComplete) {
            onComplete();
        }
    }

    function trackLoginActivity(userRole: string) {
        client('api/login-activity/', {
            method: 'POST',
            body: {
                user_role: userRole,
                location: window.location.href,
            },
        });
    }

    function updateSavedProviders(providerId: number) {
        const providerAlreadySaved = savedProviders.includes(providerId);
        if (providerAlreadySaved) {
            setSavedProviders(savedProviders.filter((id) => id !== providerId));
        } else {
            setSavedProviders([...savedProviders, providerId]);
        }
    }

    function getDisplayText(key: string, fallbackValue?: string) {
        let text = null;
        if (businessTypeTranslations && businessTypeTranslations[key]) {
            text = businessTypeTranslations[key];
        }
        return text ?? fallbackValue ?? key;
    }

    const paymentStatus = user?.businesses_active?.[0]?.payment_status;
    const isPaymentBlocked = !!paymentStatus && BLOCKED_PAYMENT_STATUSES.includes(paymentStatus as never);

    function hasPermission(permissionType: PermissionType, addressId?: number): boolean {
        if (user?.role !== 'BUSINESS_CONTACT') {
            return true;
        }

        if (permissionType === PermissionType.PostJob) {
            if (addressId) {
                return user?.address.some((x) => x.can_post_jobs && x.id === addressId) || false;
            }
            return user?.address.some((x) => x.can_post_jobs) || false;
        } else if (permissionType === PermissionType.ManagePayments) {
            if (addressId) {
                return user?.address.some((x) => x.can_manage_payments && x.id === addressId) || false;
            }
            return user?.address.some((x) => x.can_manage_payments) || false;
        } else if (permissionType === PermissionType.ManageAccount) {
            return false;
        }

        return true;
    }

    const contextValue = {
        user,
        tempToken,
        setTempToken,
        role,
        refreshUser,
        refreshAlreadyLoggedInUser,
        savedProviders,
        updateSavedProviders,
        fetchUser,
        getDisplayText,
        logout,
        hasPermission,
        isPaymentBlocked,
    };

    return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}

export function useUserContext() {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error(
            'useUserContext must be used within a UserProvider. Wrap a parent component in <UserProvider> to fix this error.',
        );
    }
    return context;
}
